import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  DialogContent,
  Dialog,
  DialogTitle,
  DialogContentText,
} from "@material-ui/core";
import {
  BarChart,
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";

import moment from "moment";
import { useTranslation } from "react-i18next";
import InfoIcon from "@material-ui/icons/Info";
import { WindRose, calculateWindRose } from "react-windrose-chart";

import httpClient from "../../lib/httpClient";

function InfoDialog(props) {
  const { t } = useTranslation();

  const { open, onClose } = props;
  const handleClose = () => {
    onClose();
  };

  const iconAndH5 = (iconNamePng, text) => {
    return (
      <div style={{ display: "flex" }}>
        <img
          src={`${process.env.PUBLIC_URL}images/icons/EnvironmentalConditions/${iconNamePng}`}
          style={{ height: 55, width: 55, marginRight: "1em" }}
          alt={iconNamePng}
        />
        <h5>{text}</h5>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t("environmentConditions.title")}</DialogTitle>
      <DialogContent>
        {iconAndH5("DailyRainfall.png", t("environmentConditions.dailyRainfall"))}
        <DialogContentText>{t("weatherDataExplained.plotRainDaily")}</DialogContentText>
        {iconAndH5("3DailyRainfall.png", t("environmentConditions.threeDayAccumulatedRainfall"))}
        <DialogContentText>{t("weatherDataExplained.showRain3Days")}</DialogContentText>
        {iconAndH5("AverageTemperature.png", t("environmentConditions.temperature"))}
        <DialogContentText>{t("weatherDataExplained.plotTemperatureDaily")}</DialogContentText>
        {iconAndH5("RadarVMI.png", t("environmentConditions.radarVmi"))}
        <DialogContentText>
          {t("weatherDataExplained.dopplerAnimation")}
          {t("weatherDataExplained.dopplerData")}
        </DialogContentText>
        {iconAndH5("RadarStormImpact.png", t("environmentConditions.radarStormImpact"))}
        <DialogContentText>
          {t("weatherDataExplained.dopplerCumulated")}
          {t("weatherDataExplained.dopplerData")}
        </DialogContentText>
        {iconAndH5("WindSpeed.png", t("environmentConditions.windSpeed"))}
        <DialogContentText>{t("weatherDataExplained.windRose")}</DialogContentText>
        {iconAndH5("Snow.png", t("environmentConditions.snow"))}
        <DialogContentText>{t("weatherDataExplained.snow")}</DialogContentText>
      </DialogContent>
    </Dialog>
  );
}

function multiplyNumeric(obj) {
  for (let key in obj) {
    if (typeof obj[key] == "number") {
      obj[key] *= 10;
    }
  }
}

const EnvironmentCondition = (props) => {
  const { t } = useTranslation();

  const [windRoseData, setWindRoseData] = useState(null);
  const [dailyObservations, setDailyObservations] = useState(null);

  const [hourlyObservations, setHourlyObservations] = useState(null);
  const [eventDateTemperature, setEventDateTemperature] = useState(0);
  const [eventDateRainfall, setEventDateRainfall] = useState(0);
  const [threeDayRainfall, setThreeDayRainfall] = useState(0);
  const [eventDateWind, setEventDateWind] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [dopplerDataPairs, setDopplerDataPairs] = useState(null);
  const [validationReport, setValidationReport] = useState({
    valid: "validating",
    msg: [t("environmentConditions.validating")],
  });

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const processWindrose = () => {
    httpClient()
      .post("/meteoblue/wind", {
        latitude: props.latitude,
        longitude: props.longitude,
        eventDate: props.eventDate,
      })
      .then(({ data }) => {
        const { winds: dailyWindsData, eventDateWind } = data;

        const direction = dailyWindsData.map((wind) => wind.windDirection);
        const speed = dailyWindsData.map((wind) => wind.windSpeed);

        const xs = calculateWindRose({
          direction,
          speed,
        });

        xs.map((x) => x).map((y) => multiplyNumeric(y));

        const windRoseData = {
          data: xs,
          columns: ["angle", "0-1", "1-2", "2-3", "3-4", "4-5", "5-6", "6-7", "7+"],
        };

        setWindRoseData(windRoseData);
        setEventDateWind(eventDateWind);
      })
      .catch((err) => console.log(err));
  };

  const computeConclusionInBackend = useCallback(async () => {
    const { latitude, longitude, eventDate, productCommercial, productSireGroup, claim, aiVersion } = props;

    httpClient()
      .post("/claimsdb/conclusion", {
        latitude,
        longitude,
        eventDate,
        aiVersion,
        productCommercial,
        productSireGroup,
        perilCode: claim.perilCode,
        adversityCode: productCommercial ? claim.claimCauseCode : claim.adversityCode,
        perilName: claim.perilName,
        adversityDescription: productCommercial ? claim.claimCauseDescription : claim.adversityDescription,
        claimCode: claim.claimCode,
        language: localStorage.getItem("language"),
      })
      .then(({ data }) => {
        setValidationReport(data);
      })
      .catch((err) => console.log("==========---> ", err));
  }, [props]);

  useEffect(() => {
    computeConclusionInBackend();
  }, [computeConclusionInBackend]);

  const processMeteoblueTemperature = async () => {
    const { latitude, longitude, eventDate } = props;

    httpClient()
      .post("/meteoblue", {
        latitude,
        longitude,
        eventDate,
      })
      .then(({ data }) => {
        const {
          temperatures: dailyObservationsData,
          hourlyTemperatures,
          eventDateTemperature,
          threeDayRainfall,
        } = data;

        setDailyObservations(dailyObservationsData);
        setHourlyObservations(hourlyTemperatures);
        setEventDateTemperature(eventDateTemperature);
        setEventDateRainfall(eventDateTemperature);

        setThreeDayRainfall(threeDayRainfall);
      })
      .catch((err) => console.log("==========---> ", err));
  };

  useEffect(() => {
    processMeteoblueTemperature();
    processWindrose();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.eventDate, selectedIndex]);

  useEffect(() => {
    if (!props.dopplerData) return;
    // How manu elements in dopplerData object ?
    const steps = Object.keys(props.dopplerData).length - 1; // 12 or 48

    // http://localhost:3000/#/claimsagriculture/710409/133966 for 12
    // http://localhost:3000/#/claimsagriculture/696273/134051 for 48
    // http://localhost:3000/#/claimsagriculture/685614/113956 for 0

    // http://localhost:3000/#/newclaims/202200000031777600?perilCode=17  VALID, radar 0
    // http://localhost:3000/#/newclaims/202300000042192200?perilCode=11  INVALID, radar 12
    // http://localhost:3000/#/newclaims/202300000043479500?perilCode=17  MISSING temperature DATA, 48 radar frames, VMI=0
    // http://localhost:3000/#/newclaims/202400000000351000?perilCode=41  RADAR demo data, 48 radar frames, VMI>0

    const stepArray = [...Array(steps).keys()];
    const dopplerDataPrepared = stepArray.map((x, i) => {
      // timestep is in format HH:MM and it is fraction of 24 hours divided by steps
      const duration = moment.duration((60 * 24 * i) / steps, "minutes");
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.minutes());
      const timeStep = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      return { name: timeStep, value: Math.max(0, parseFloat(props.dopplerData[i]) || 0) };
    });

    setDopplerDataPairs(dopplerDataPrepared);
  }, [props.dopplerData]);

  const handleListItemClick = (event, index) => {
    setSelectedIndex(index);

    props.updateShowDbz(false);
    props.updateShowVmiDbz(false);
    if (index === 0) {
    } else if (index === 1) {
      // 3 days accumulated rainfall
    } else if (index === 2) {
      // 7 days accumulated rainfall
      props.updateShowRainfallData(true);
    } else if (index === 3) {
      // VMI-dBZ , animation
      if (props.dopplerFrequency !== 0) props.updateShowVmiDbz(true);
    } else if (index === 4) {
      // dBZ
      if (props.dopplerFrequency !== 0) props.updateShowDbz(true);
    } else if (index === 5) {
      // Wind speed
      processWindrose();
    } else if (index === 6) {
      // Show temperature
    } else if (index === 7) {
      // Show hourly temperature
    } else if (index === 8) {
      // Show hourly rainfall
    }
  };

  const plotDopplerDataPairs = (dopplerDataPairs) => {
    if (dopplerDataPairs && dopplerDataPairs.length > 0) {
      return (
        <div style={{ height: "420px" }}>
          <ResponsiveContainer>
            <LineChart data={dopplerDataPairs} marginRight={5}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                label={{
                  value: "Tics in GMT",
                  dx: 100,
                  dy: 25,
                }}
              />
              <YAxis
                label={{
                  value: "dBZ",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stackId="1" stroke="#8884d8" fill="#8884d8" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    } else if (props.dopplerData) {
      return (
        <Box ml={2} mb={2}>
          Computing, please wait (it can take 10-20 sec.) ...
        </Box>
      );
    } else {
      return (
        <Box ml={2} mb={2}>
          No radar data available for this claim. <br />
          Data is collected from 2023-05-17 only. <br />
          No plot data available 2023-05-24 - 2023-06-09.
          <br />
          No data available 2023-07-06 - 2023-07-18.
        </Box>
      );
    }
  };

  const renderValidation = () => (
    <ul>
      {validationReport.msg.map((s, index) => (s === "" ? <br key={index} /> : <li key={index}>{s}</li>))}
    </ul>
  );

  const validationIcon = () => {
    // Motivation: Even if the validation is unknown, we want to show the green thumbs up, in order to keep only two-thumbs-logic.
    // Thumb down is intended for case that clearly invalid and require human intervention.

    if (validationReport.valid) {
      if (validationReport.valid === "unknown") {
        return "ThumbsUp.png";
      } else if (validationReport.valid === "validating") {
        return "ThumbsUnknown.png";
      } else if (validationReport.valid === "missingData") {
        return "ThumbsUnknown.png";
      } else if (validationReport.valid === "skip") {
        return "ThumbsUp.png";
      } else {
        // True
        return "ThumbsUp.png";
      }
    } else {
      return "ThumbsDown.png";
    }
  };

  const plotTemperature = (temperature) => {
    return (
      <div style={{ height: "420px" }}>
        <ResponsiveContainer>
          <LineChart data={temperature} marginRight={5}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis
              label={{
                value: "Temperature (˚C)",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip />
            <Legend />
            {/* There is internal bug in Rechart: minimal must go first. */}
            <Line type="monotone" dataKey="minTemp" stackId="1" stroke="#ffc658" fill="#ffc658" />
            <Line type="monotone" dataKey="medTemp" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
            <Line type="monotone" dataKey="maxTemp" stackId="1" stroke="#8884d8" fill="#8884d8" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const plotHourlyTemperature = (hourlyObservations) => {
    let hourlyObservationsWithShortDate = hourlyObservations.map((x) => {
      return { ...x, shortDate: makeShortDate(x.localDateAndTime) };
    });

    // Take every 24 th element from hourlyObservations
    let hourlyTemperatureFiltered = filterMidnightDates(hourlyObservationsWithShortDate);

    const ticks = hourlyTemperatureFiltered.map((x) => x.shortDate);

    // const sampleTicks = ["29/06/2022 08:00", "04/07/2022 08:00"];
    return (
      <div style={{ height: "420px" }}>
        <ResponsiveContainer>
          <LineChart data={hourlyObservationsWithShortDate} marginRight={5}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="shortDate"
              height={55}
              tick={{ angle: -90, dy: 25, dx: 25 }}
              ticks={ticks}
              label={{
                value: "Tics at 00:00 (CEST)",
                dx: 100,
                dy: 40,
              }}
            />
            <YAxis
              label={{
                value: "Temperature (˚C)",
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip />
            <Legend />
            {/* There is internal bug in Rechart: minimal must go first. */}
            <ReferenceLine x={ticks[6]} stroke="red" />
            <ReferenceLine x={ticks[7]} stroke="red" />
            <Line type="monotone" dataKey="hourlyTemp" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const conditionIcon = (png) => (
    <ListItemIcon>
      <img
        src={`${process.env.PUBLIC_URL}images/icons/EnvironmentalConditions/${png}`}
        style={{ height: 40, width: 40, marginRight: "2em" }}
        alt=""
      />
    </ListItemIcon>
  );

  const plotDailyRainFall = (rainfall) => {
    return (
      <div style={{ height: "420px" }}>
        <ResponsiveContainer>
          <BarChart marginRight={"auto"} marginLeft={"auto"} data={rainfall}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis
              label={{
                value: `${t("environmentConditions.dailyRainfall") + " (NEMS4) (mm) "}`,
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip />
            <Legend />
            <Bar dataKey="rainfall" barSize={30} fontFamily="sans-serif">
              {rainfall &&
                rainfall.map((entry, index) => (
                  <Cell
                    fill={entry.date === moment(props.eventDate).format("DD/MM/YYYY") ? "#d41002" : "#089c19"}
                    key={index}
                  />
                ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const filterMidnightDates = (observations) => {
    console.log("observations", observations);
    // Design note: this is fragile function.
    // The midnight dates have "00:00"
    // and they are formatted as DD/MM.
    // Others are formatted differently, as DD/MM/YYYY HH:mm.
    const filteredMidnightDates = observations.filter((x) => x.localDateAndTime.includes("00:00"));
    console.log("filteredMidnightDates", filteredMidnightDates);
    return filteredMidnightDates;
  };

  const makeShortDate = (t) => {
    console.log("t", t);
    const validDate = moment(t, "DD/MM/YYYY HH:mm");
    const hour = moment(validDate).format("HH");
    return hour === "00"
      ? moment(validDate).format("DD/MM")
      : moment(validDate).format("DD/MM/YYYY,HH:mm") + " CEST";
  };

  const plotHourlyRainFall = (hourlyObservations) => {
    let hourlyObservationsWithShortDate = hourlyObservations.map((x) => {
      return { ...x, shortDate: makeShortDate(x.localDateAndTime) };
    });

    let hourlyTemperatureFiltered = filterMidnightDates(hourlyObservationsWithShortDate);

    const ticks = hourlyTemperatureFiltered.map((x) => x.shortDate);
    return (
      <div style={{ height: "420px" }}>
        <ResponsiveContainer>
          <BarChart marginRight={"auto"} marginLeft={"auto"} data={hourlyObservationsWithShortDate}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="shortDate"
              height={55}
              tick={{ angle: -90, dy: 25, dx: 25 }}
              ticks={ticks}
              label={{
                value: "Tics at 00:00 (CEST)",
                dx: 100,
                dy: 40,
              }}
            />
            <YAxis
              label={{
                value: `${t("environmentConditions.hourlyRainfall") + " (NEMS4)"} (mm)`,
                angle: -90,
                position: "insideLeft",
              }}
            />
            <Tooltip />
            <Legend />
            <ReferenceLine x={ticks[6]} stroke="red" />
            <ReferenceLine x={ticks[7]} stroke="red" />
            <Bar dataKey="hourlyRainfall" barSize={30} fontFamily="sans-serif" fill="#089c19"></Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const conditionsCard = () => (
    <Card>
      <CardHeader title={t("environmentConditions.title")} />
      <Paper style={{ height: "500px", overflow: "auto" }}>
        <List component="nav" dense={true}>
          {/* 0. Daily Rainfall (Ground Station) */}
          <ListItem button selected={selectedIndex === 0} onClick={(event) => handleListItemClick(event, 0)}>
            {conditionIcon("DailyRainfall.png")}
            <ListItemText
              primary={`${t("environmentConditions.dailyRainfall")} (NEMS4)`}
              secondary={`${(eventDateRainfall && eventDateRainfall.rainfall?.toFixed(2)) || 0} mm`}
            />
          </ListItem>

          {/* 1. 3 days Rainfall */}
          <ListItem button selected={selectedIndex === 1} onClick={(event) => handleListItemClick(event, 1)}>
            {conditionIcon("3DailyRainfall.png")}
            <ListItemText
              primary={t("environmentConditions.threeDayAccumulatedRainfall") + " (NEMS4) "}
              secondary={`${(threeDayRainfall && threeDayRainfall.toFixed(2)) || 0} mm`}
            />
          </ListItem>

          {/* 8. Hourly Rainfall */}
          <ListItem button selected={selectedIndex === 8} onClick={(event) => handleListItemClick(event, 8)}>
            {conditionIcon("DailyRainfall.png")}
            <ListItemText
              primary={t("environmentConditions.hourlyRainfall") + "  (NEMS4)"}
              secondary={`${(eventDateRainfall && eventDateRainfall.rainfall?.toFixed(2)) || 0} mm`}
            />
          </ListItem>

          {/* 2. VMI-dBZ */}

          <ListItem button selected={selectedIndex === 3} onClick={(event) => handleListItemClick(event, 3)}>
            {conditionIcon("RadarVMI.png")}
            <ListItemText
              primary="Radar - Vertical Maximum Intensity (VMI-dBZ)"
              secondary={`${props.dopplerData ? "24h animation" : "N/A"} `}
            />
          </ListItem>

          {/* 3. dBZ */}

          <ListItem button selected={selectedIndex === 4} onClick={(event) => handleListItemClick(event, 4)}>
            {conditionIcon("RadarStormImpact.png")}
            <ListItemText
              primary={`${t("environmentConditions.radarStormImpact")} (dBZ)`}
              secondary={`${props.dopplerData ? props.dopplerData.max_vmi : "N/A"} dBZ`}
            />
          </ListItem>

          {/* 4. windSpeed*/}
          <ListItem button selected={selectedIndex === 5} onClick={(event) => handleListItemClick(event, 5)}>
            {conditionIcon("WindSpeed.png")}
            <ListItemText
              primary={t("environmentConditions.windSpeed")}
              secondary={`${(eventDateWind && eventDateWind.windSpeed.toFixed(2)) || 0} km/h`}
            />
          </ListItem>
          {/* 5. temperature */}
          <ListItem button selected={selectedIndex === 6} onClick={(event) => handleListItemClick(event, 6)}>
            <Box>
              <Box display="flex">
                <Box>{conditionIcon("AverageTemperature.png")}</Box>
                <Box>
                  <ListItemText
                    primary={t("environmentConditions.dailyTemperature")}
                    secondary={`Min=${
                      (eventDateTemperature && eventDateTemperature?.minTemp?.toFixed(2)) || "N/A"
                    } °C  , Med=${
                      (eventDateTemperature && eventDateTemperature?.medTemp?.toFixed(2)) || "N/A"
                    } °C  , Max=${
                      (eventDateTemperature && eventDateTemperature?.maxTemp?.toFixed(2)) || "N/A"
                    } °C`}
                  />
                </Box>
              </Box>
            </Box>
          </ListItem>

          {/* 7. hourlyObservations */}
          <ListItem button selected={selectedIndex === 7} onClick={(event) => handleListItemClick(event, 7)}>
            {conditionIcon("AverageTemperature.png")}
            <ListItemText primary={t("environmentConditions.hourlyTemperature")} />
          </ListItem>
        </List>
      </Paper>
    </Card>
  );

  return (
    <Grid container style={{ marginTop: 15 }}>
      <Grid item xs={12} sm={4}>
        <Box ml={1} mr={1}>
          {conditionsCard()}
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Box ml={1} mr={1}>
          <Card style={{ height: "500px" }}>
            <CardHeader
              action={
                <IconButton onClick={handleClickOpen}>
                  <InfoIcon />
                </IconButton>
              }
            />
            {(selectedIndex === 3 || selectedIndex === 4) && plotDopplerDataPairs(dopplerDataPairs)}

            {/* Linechart for temperature */}
            {selectedIndex === 6 &&
              dailyObservations &&
              dailyObservations.length > 0 &&
              plotTemperature(dailyObservations)}
            {selectedIndex === 7 &&
              hourlyObservations &&
              hourlyObservations.length > 0 &&
              plotHourlyTemperature(hourlyObservations)}

            {/* Rainfall Chart */}
            {(selectedIndex === 0 || selectedIndex === 1) &&
              dailyObservations &&
              dailyObservations.length > 0 &&
              plotDailyRainFall(dailyObservations)}

            {selectedIndex === 8 &&
              hourlyObservations &&
              hourlyObservations.length > 0 &&
              plotHourlyRainFall(hourlyObservations)}

            {/* Windspeed Chart */}
            {selectedIndex === 5 && windRoseData.data && windRoseData.data.length > 0 && (
              <div
                style={{
                  height: "420px",
                  display: "block",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <WindRose data={windRoseData.data} columns={windRoseData.columns} height={400} />
              </div>
            )}

            {/* Dopper radar storm trail iamge */}
            {selectedIndex === 4 && <div></div>}

            {/* Dopper radar image animation */}
            {selectedIndex === 3 && <div></div>}

            {/* show message for 7 days accumulated rainfall */}
            {selectedIndex === 2 && (
              <div
                style={{
                  display: "flex",
                  flexFlow: "wrap",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  alignContent: "center",
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}images/up-map.png`}
                  style={{ height: 120, width: 120 }}
                  alt=""
                />
                <p style={{ marginLeft: 20, marginTop: 20, marginRight: 20 }}>
                  {t("environmentConditions.mapZoomInfo")}
                </p>
              </div>
            )}
          </Card>

          <InfoDialog open={open} onClose={handleClose} />
        </Box>
      </Grid>

      <Grid container p={1}>
        <Grid item xs={12} sm={12}>
          <Box ml={1} mr={1}>
            {validationReport.valid === "missingData" ? (
              <Card>
                <CardHeader title={t("aiAssistant.dataMissing")} />
              </Card>
            ) : (
              <Card>
                <CardHeader title={t("aiAssistant.claimValidation")} />
                <Box display="flex" justifyContent="center" alignItems="center">
                  <img
                    src={`${process.env.PUBLIC_URL}images/icons/Others/AiAssistant.png`}
                    style={{ height: 70, width: 70, margin: "1em" }}
                    alt=""
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}images/icons/Others/${validationIcon()}`}
                    style={{ height: 85, width: 85 }}
                    alt=""
                  />
                  {renderValidation()}
                </Box>
              </Card>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box></Box>
    </Grid>
  );
};

export default EnvironmentCondition;
